// /app/frontend/src/components/frugal-v2/components/ReceivablePayableSection.jsx
// Compact list of receivables OR payables (accounts_receivable / accounts_payable).
// Each item = name + balance, editable inline, plus a "+" to add.

import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Check, X } from 'lucide-react';
import * as api from '../services/frugalApi';

const ReceivablePayableSection = ({
  token,
  t,
  type, // 'receivable' | 'payable'
  items,
  onChange,
}) => {
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', balance: '' });
  const [adding, setAdding] = useState(false);

  const isReceivable = type === 'receivable';
  const total = items.reduce((s, a) => s + (a.balance || 0), 0);
  const titleKey = isReceivable
    ? 'frugal.accounts.receivables'
    : 'frugal.accounts.payables';
  const accentColor = isReceivable ? 'text-sky-400' : 'text-rose-400';
  const iconBg = isReceivable ? 'bg-sky-500/15' : 'bg-rose-500/15';

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      balance: (item.balance ?? 0).toFixed(2),
    });
  };

  const submitEdit = async () => {
    try {
      await api.updateAccount(token, editingId, {
        name: form.name.trim() || 'Sin nombre',
        balance: parseFloat(form.balance) || 0,
      });
      setEditingId(null);
      onChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const submitAdd = async () => {
    if (!form.name.trim()) return setAdding(false);
    try {
      await api.createAccount(token, {
        name: form.name.trim(),
        balance: parseFloat(form.balance) || 0,
        account_type: type,
        is_default: false,
      });
      setAdding(false);
      setForm({ name: '', balance: '' });
      onChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const remove = async (id) => {
    try {
      await api.deleteAccount(token, id);
      onChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div
      className="bg-white/5 rounded-xl p-4 border border-white/10"
      data-testid={`accounts-${type}-section`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
            <span className={`text-xs font-bold ${accentColor}`}>{isReceivable ? '↙' : '↗'}</span>
          </div>
          <h3 className="text-white font-medium">{t(titleKey)}</h3>
        </div>
        <span className={`font-medium ${accentColor}`}>
          ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      </div>

      <ul className="space-y-1.5">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5"
          >
            {editingId === item.id ? (
              <>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="flex-1 min-w-0 px-2 py-1 bg-white/10 border border-white/20 text-white text-sm rounded"
                  data-testid={`edit-${type}-name-${item.id}`}
                />
                <span className="text-slate-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={form.balance}
                  onChange={(e) => setForm({ ...form, balance: e.target.value })}
                  className="w-24 px-2 py-1 bg-white/10 border border-white/20 text-white text-sm rounded text-right"
                  data-testid={`edit-${type}-balance-${item.id}`}
                />
                <button
                  onClick={submitEdit}
                  className="p-1.5 text-emerald-400 hover:bg-white/10 rounded"
                  data-testid={`save-${type}-${item.id}`}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditingId(null)}
                  className="p-1.5 text-slate-400 hover:bg-white/10 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            ) : (
              <>
                <span className="flex-1 min-w-0 text-sm text-slate-200 truncate">{item.name}</span>
                <span className={`text-sm font-medium ${accentColor}`}>
                  ${(item.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <button
                  onClick={() => startEdit(item)}
                  className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded"
                  data-testid={`edit-${type}-${item.id}`}
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => remove(item.id)}
                  className="p-1.5 text-slate-400 hover:text-rose-400 hover:bg-white/10 rounded"
                  data-testid={`del-${type}-${item.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </li>
        ))}

        {adding && (
          <li className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5">
            <input
              autoFocus
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t('frugal.accounts.newEntry', 'Nombre')}
              className="flex-1 min-w-0 px-2 py-1 bg-white/10 border border-white/20 text-white text-sm rounded"
              data-testid={`new-${type}-name`}
            />
            <span className="text-slate-400">$</span>
            <input
              type="number"
              step="0.01"
              value={form.balance}
              onChange={(e) => setForm({ ...form, balance: e.target.value })}
              placeholder="0.00"
              className="w-24 px-2 py-1 bg-white/10 border border-white/20 text-white text-sm rounded text-right"
              data-testid={`new-${type}-balance`}
            />
            <button
              onClick={submitAdd}
              className="p-1.5 text-emerald-400 hover:bg-white/10 rounded"
              data-testid={`create-${type}`}
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setAdding(false); setForm({ name: '', balance: '' }); }}
              className="p-1.5 text-slate-400 hover:bg-white/10 rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </li>
        )}
      </ul>

      {!adding && (
        <button
          onClick={() => { setAdding(true); setForm({ name: '', balance: '' }); }}
          className="mt-3 w-full flex items-center justify-center gap-2 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-slate-300 text-sm"
          data-testid={`add-${type}-btn`}
        >
          <Plus className="w-4 h-4" />
          {t('frugal.accounts.addEntry', 'Agregar')}
        </button>
      )}
    </div>
  );
};

export default ReceivablePayableSection;
